import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { primitiveColors } from './template';

/**
 * Стили React-острова в island-архитектуре.
 *
 * Зачем нужно
 * -----------
 * Острова рендерятся внутрь легаси-сайта с Bootstrap 3 + normalize.css.
 * Стили текут в обе стороны: легаси портит React (HiddenInput виден,
 * `<a>` синий по Bootstrap), React портит легаси (`form, input { width: 100% }`).
 * CSS-скоупинг через атрибут не справляется с Bootstrap: его
 * element-селекторы (0,0,1)–(0,0,2) либо проигрывают нашему `:where()`
 * (0,0,0), либо для победы нужно поднять специфичность так, что
 * перебивает собственные styled-компоненты.
 *
 * Архитектурный ответ — Shadow DOM (см. `src/styles/ShadowMount.tsx`).
 * Реальная изоляция: легаси физически не достаёт внутрь, наши стили
 * не выходят наружу.
 *
 * Что экспортируется
 * ------------------
 * 1. `InsideShadowReset` — `createGlobalStyle`, который монтируется
 *    **внутри ShadowMount** и применяется только в пределах shadow.
 *    Селекторы обычные (`a`, `button`, `*`) без `:where()` и без
 *    префиксов — никакой битвы за специфичность, потому что внутри
 *    shadow нет легаси. Класс styled-компонентов (0,1,0) спокойно
 *    бьёт element-селектор (0,0,1).
 *
 *    `:host { font-family: Roboto; font-size: 2rem; color: ... }` —
 *    типография «приземляется» на shadow host и наследуется внутрь,
 *    перебивая то, что легаси-сайт навесил на `<body>`.
 *
 * 2. `IslandScopeReset` — `createGlobalStyle` со скоупом
 *    `:where([data-island-scope]) :where(...)` — специфичность (0,0,0).
 *    **Используется только в Storybook и в `renderWithProviders`**,
 *    где нет shadow и нет легаси-Bootstrap: достаточно лёгкого
 *    element-level reset'а в пределах wrapper'а `IslandScope`.
 *
 * 3. `IslandScope` (default) — `styled.div data-island-scope` с
 *    `display: contents` и базовой типографией. Wrapper для Storybook
 *    и тестов (даёт `[data-island-scope]` атрибут для IslandScopeReset
 *    и font-family для inheritance). В проде внутри ShadowMount тоже
 *    можно оборачивать им children — display: contents безвреден,
 *    а атрибут просто игнорируется внутри shadow.
 *
 * `global.ts` остаётся только с document-level правилами:
 * `html { font-size: 8px }` (база rem), `body { ... }`, scrollbar и т.п.
 */

/**
 * Reset для использования ВНУТРИ Shadow DOM. Селекторы обычные —
 * Bootstrap и нормализация физически не достают сюда, специфичность
 * можно не накручивать.
 *
 * Монтируется как `<InsideShadowReset />` внутри `<ShadowMount>` —
 * styled-components кладёт его в `<style>`-тег самого shadow root,
 * а не в `<head>` документа.
 */
export const InsideShadowReset = createGlobalStyle`
    :host {
        display: contents;
        font-family: 'Roboto', sans-serif;
        font-size: 2rem;
        font-weight: 400;
        /* 1.5 — базовое отношение для латиницы/кириллицы; --lh-base поднимает
           его для «высоких» письменностей (см. html[lang="hi"] в main.css). */
        line-height: var(--lh-base, 1.5);
        color: ${({ theme }) => theme.semantic.text.primary ?? primitiveColors.gray[500]};
    }

    *, *::before, *::after {
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
    }

    /* Выделение курсивом — через переменные (значения задаёт html[lang="…"] в
       bx/main.css). По умолчанию повторяет UA: наклон и унаследованный вес.
       Письменности без курсива (арабица) заменяют наклон весом. Кастомные
       свойства наследуются сквозь границу тени, поэтому остров подхватывает
       их без своей копии значений. Селектор i берём только без класса:
       <i class="icon-*"> в проекте — иконки. */
    em,
    i:not([class]),
    cite,
    address {
        font-style: var(--em-style, italic);
        font-weight: var(--em-weight, inherit);
    }

    div, span, iframe,
    h1, h2, h3, h4, h5, h6, p, blockquote, pre,
    a, abbr, acronym, address, big, cite, code,
    del, dfn, em, img, ins, kbd, q, s, samp,
    small, strike, strong, sub, sup, tt, var,
    b, u, i, center,
    dl, dt, dd, ol, ul, li,
    fieldset, form, label, legend,
    table, caption, tbody, tfoot, thead, tr, th, td,
    article, aside, canvas, details, embed,
    figure, figcaption, footer, header, hgroup,
    menu, nav, output, ruby, section, summary,
    time, mark, audio, video {
        margin: 0;
        padding: 0;
        border: 0;
        vertical-align: baseline;
    }

    textarea {
        overflow: auto;
        resize: vertical;
    }

    hr {
        height: 0;
        overflow: visible;
    }

    main {
        display: block;
    }

    ul[class], nav ul,
    ol[class], nav ol {
        list-style: none;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }

    [type="checkbox"],
    [type="radio"] {
        padding: 0;
    }

    [type="search"] {
        -webkit-appearance: textfield;
        outline-offset: -2px;
    }

    pre {
        font-family: monospace, monospace;
        font-size: 1em;
        overflow: initial;
    }

    a {
        text-decoration: none;
        background-color: transparent;
        color: ${({ theme }) => theme.semantic.text.primary ?? primitiveColors.gray[500]};
    }

    iframe {
        border-style: none;
    }

    img {
        max-width: 100%;
        display: block;
        border-style: none;
    }

    svg:not(:root) {
        overflow: hidden;
    }

    table {
        border-collapse: collapse;
    }

    input, button, textarea, select {
        font: inherit;
        /* Контролы — своя грань и свой минимум строки (пилот ur, 2026-09-19: контент
           набран насталиком, кнопки и поля — насхом; переменные задаёт html[lang="ur"]
           в bx/main.css, там же :root { --lh-min-ui: var(--lh-min, 0px) }). Стоит ПОСЛЕ
           «font: inherit» — шорткат иначе вернул бы семейство родителя. У прочих языков
           --font-ui не задан → inherit, а --lh-min-ui равен --lh-min — ни один пиксель
           не меняется. */
        font-family: var(--font-ui, inherit);
        --lh-min: var(--lh-min-ui, 0px);
        --lh-min-head: var(--lh-min-ui, 0px);
        border: none;
        outline: none;
    }

    form, input, select, textarea {
        width: 100%;
    }

    input[type=submit],
    input[type=reset],
    input[type=button],
    button {
        padding: 0;
        border: none;
        overflow: visible;
        text-transform: none;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        transition: 220ms all ease-in-out;
        white-space: nowrap;
        vertical-align: middle;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        background: none;
    }

    input[type=submit]::-moz-focus-inner,
    input[type=reset]::-moz-focus-inner,
    input[type=button]::-moz-focus-inner,
    button::-moz-focus-inner {
        padding: 0;
    }

    input[type=submit]:focus,
    input[type=reset]:focus,
    input[type=button]:focus,
    button:focus,
    input:focus {
        outline: 0;
    }

    input[type=submit]:disabled,
    input[type=reset]:disabled,
    input[type=button]:disabled,
    button:disabled {
        cursor: not-allowed;
    }

    input[type="number"] {
        -webkit-appearance: textfield;
        -moz-appearance: textfield;
        appearance: textfield;
    }

    button,
    [type="button"],
    [type="reset"],
    [type="submit"] {
        -webkit-appearance: button;
    }

    ::-moz-focus-inner {
        border-style: none;
        padding: 0;
    }

    ::placeholder {
        color: ${({ theme }) => theme.semantic.text.secondary ?? primitiveColors.gray[400]};
        opacity: 1;
    }

    :-ms-input-placeholder {
        color: ${({ theme }) => theme.semantic.text.secondary ?? primitiveColors.gray[400]};
    }

    ::-ms-input-placeholder {
        color: ${({ theme }) => theme.semantic.text.secondary ?? primitiveColors.gray[400]};
    }

    .rec.rec-dot,
    .rec.rec-dot.rec-dot_active {
        box-shadow: none;
        border: none;
    }

    .rec.rec-dot {
        background: ${primitiveColors.gray[50]};
        width: 1rem;
        height: 1rem;
        margin: 0.5rem;
    }

    .rec.rec-dot.rec-dot_active {
        background: ${primitiveColors.accent[500]};
        width: 3rem;
        border-radius: 1rem;
    }

    div.rec-slider-container {
        margin: 0;
    }

    .lazy-image-container {
        margin: 0 auto;
        max-width: 100%;
    }

    .table-wrapper {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    /*
     * Кастомные скроллбары. Лежат здесь, а не только в global.ts, потому
     * что shadow DOM физически не видит стили из document <head>. Без
     * этого блока в меню react-select, в StyledCode и других скроллящихся
     * контейнерах под shadow появлялся дефолтный системный скроллбар.
     */
    ::-webkit-scrollbar {
        width: 6px;
    }

    ::-webkit-scrollbar:horizontal {
        height: 0.5rem;
    }

    ::-webkit-scrollbar-track {
        background: transparent !important;
        border-radius: 10px;
        box-shadow: none;
    }

    ::-webkit-scrollbar-thumb {
        background: ${primitiveColors.gray[400]};
        border-radius: 10px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: ${primitiveColors.accent[500]};
    }
`;
export const IslandScopeReset = createGlobalStyle`
    :where([data-island-scope]) *,
    :where([data-island-scope]) *::before,
    :where([data-island-scope]) *::after {
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
    }

    :where([data-island-scope]) :where(
        div, span, iframe,
        h1, h2, h3, h4, h5, h6, p, blockquote, pre,
        a, abbr, acronym, address, big, cite, code,
        del, dfn, em, img, ins, kbd, q, s, samp,
        small, strike, strong, sub, sup, tt, var,
        b, u, i, center,
        dl, dt, dd, ol, ul, li,
        fieldset, form, label, legend,
        table, caption, tbody, tfoot, thead, tr, th, td,
        article, aside, canvas, details, embed,
        figure, figcaption, footer, header, hgroup,
        menu, nav, output, ruby, section, summary,
        time, mark, audio, video
    ) {
        margin: 0;
        padding: 0;
        border: 0;
        vertical-align: baseline;
    }

    :where([data-island-scope]) :where(textarea) {
        overflow: auto;
        resize: vertical;
    }

    :where([data-island-scope]) :where(hr) {
        height: 0;
        overflow: visible;
    }

    :where([data-island-scope]) :where(main) {
        display: block;
    }

    :where([data-island-scope]) :where(ul[class]),
    :where([data-island-scope]) :where(nav) :where(ul),
    :where([data-island-scope]) :where(ol[class]),
    :where([data-island-scope]) :where(nav) :where(ol) {
        list-style: none;
    }

    :where([data-island-scope]) :where(input)::-webkit-outer-spin-button,
    :where([data-island-scope]) :where(input)::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    :where([data-island-scope]) :where(input[type=number]) {
        -moz-appearance: textfield;
    }

    :where([data-island-scope]) :where([type="checkbox"]),
    :where([data-island-scope]) :where([type="radio"]) {
        padding: 0;
    }

    :where([data-island-scope]) :where([type="search"]) {
        -webkit-appearance: textfield;
        outline-offset: -2px;
    }

    :where([data-island-scope]) :where(pre) {
        font-family: monospace, monospace;
        font-size: 1em;
        overflow: initial;
    }

    :where([data-island-scope]) :where(a) {
        text-decoration: none;
        background-color: transparent;
        color: ${({ theme }) => theme.semantic.text.primary ?? primitiveColors.gray[500]};
    }

    :where([data-island-scope]) :where(iframe) {
        border-style: none;
    }

    :where([data-island-scope]) :where(img) {
        max-width: 100%;
        display: block;
        border-style: none;
    }

    :where([data-island-scope]) :where(svg):not(:root) {
        overflow: hidden;
    }

    :where([data-island-scope]) :where(table) {
        border-collapse: collapse;
    }

    :where([data-island-scope]) :where(input, button, textarea, select) {
        font: inherit;
        border: none;
        outline: none;
    }

    :where([data-island-scope]) :where(form, input, select, textarea) {
        width: 100%;
    }

    :where([data-island-scope]) :where(
        input[type=submit],
        input[type=reset],
        input[type=button],
        button
    ) {
        padding: 0;
        border: none;
        overflow: visible;
        text-transform: none;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        transition: 220ms all ease-in-out;
        white-space: nowrap;
        vertical-align: middle;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        background: none;
    }

    :where([data-island-scope]) :where(
        input[type=submit],
        input[type=reset],
        input[type=button],
        button
    )::-moz-focus-inner {
        padding: 0;
    }

    :where([data-island-scope]) :where(
        input[type=submit],
        input[type=reset],
        input[type=button],
        button,
        input
    ):focus {
        outline: 0;
    }

    :where([data-island-scope]) :where(
        input[type=submit],
        input[type=reset],
        input[type=button],
        button
    ):disabled {
        cursor: not-allowed;
    }

    :where([data-island-scope]) :where(input[type="number"]) {
        -webkit-appearance: textfield;
        -moz-appearance: textfield;
        appearance: textfield;
    }

    :where([data-island-scope]) :where(button, [type="button"], [type="reset"], [type="submit"]) {
        -webkit-appearance: button;
    }

    :where([data-island-scope]) ::-moz-focus-inner {
        border-style: none;
        padding: 0;
    }

    :where([data-island-scope]) ::placeholder {
        color: ${({ theme }) => theme.semantic.text.secondary ?? primitiveColors.gray[400]};
        opacity: 1;
    }

    :where([data-island-scope]) :-ms-input-placeholder {
        color: ${({ theme }) => theme.semantic.text.secondary ?? primitiveColors.gray[400]};
    }

    :where([data-island-scope]) ::-ms-input-placeholder {
        color: ${({ theme }) => theme.semantic.text.secondary ?? primitiveColors.gray[400]};
    }

    /* react-elastic-carousel — точки навигации */
    :where([data-island-scope]) :where(.rec.rec-dot),
    :where([data-island-scope]) :where(.rec.rec-dot.rec-dot_active) {
        box-shadow: none;
        border: none;
    }

    :where([data-island-scope]) :where(.rec.rec-dot) {
        background: ${primitiveColors.gray[50]};
        width: 1rem;
        height: 1rem;
        margin: 0.5rem;
    }

    :where([data-island-scope]) :where(.rec.rec-dot.rec-dot_active) {
        background: ${primitiveColors.accent[500]};
        width: 3rem;
        border-radius: 1rem;
    }

    :where([data-island-scope]) :where(div.rec-slider-container) {
        margin: 0;
    }

    :where([data-island-scope]) :where(.lazy-image-container) {
        margin: 0 auto;
        max-width: 100%;
    }

    :where([data-island-scope]) :where(.table-wrapper) {
        width: 100%;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }
`;

/**
 * Обёртка-маркер: задаёт базовую типографию через inheritance
 * (display: contents — собственного layout-бокса не вводит).
 * Атрибут `data-island-scope` пробрасываем в JSX компонента ниже —
 * именно по нему таргетится `IslandScopeReset`.
 *
 * Сам элемент НЕ содержит element-level правил — они в
 * `IslandScopeReset` (createGlobalStyle), чтобы не раздуть
 * специфичность и не побить компонентные стили.
 */
const Scope = styled.div`
    display: contents;
    font-family: 'Roboto', sans-serif;
    font-size: 2rem;
    font-weight: 400;
    /* 1.5 — базовое отношение для латиницы/кириллицы; --lh-base поднимает
       его для «высоких» письменностей (см. html[lang="hi"] в main.css). */
    line-height: var(--lh-base, 1.5);
    color: ${({ theme }) => theme.semantic.text.primary ?? primitiveColors.gray[500]};
`;

interface Props {
	children: React.ReactNode;
}

const IslandScope: React.FC<Props> = ({ children }) => (
	<Scope data-island-scope="">{children}</Scope>
);

export default IslandScope;
